import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { queryKeys } from '@/constants/query-key';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import { useDrives } from '@/features/driver/hooks/use-drives';
import type { OperationFinishConfirmData } from '@/features/driver/types/driveDetail';
import { getTimelineData } from '@/features/driver/utils/processData';
import { useUpdate } from '@/modules/hooks/use-put';
import * as paths from '@/routes/paths';

type Param = {
  vehicleOperationScheduleId: string;
  canceledPassengerCount: number;
  canceledReservationCount: number;
  canceledPrice: number;
};

type OperationFinishConfirmPageProps = {
  storybookMode?: boolean;
  mockDrives?: OperationFinishConfirmData;
};

export const OperationFinishConfirmPage: React.FC<OperationFinishConfirmPageProps> = ({
  storybookMode = false,
  mockDrives,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [drives, setDrives] = useState<OperationFinishConfirmData>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isButtonShow, setIsButtonShow] = useState(false);

  const { vehicleOperationScheduleId } = (location.state as Param) || {};

  const { data, error, isLoading } = useDrives(storybookMode ? '' : vehicleOperationScheduleId);
  const reservationCount = drives?.driveReservationCount || 0;
  const passengerCount = drives?.drivePassengerCount || 0;
  const totalAmount = drives?.drivePrice || 0;

  const { mutate, isLoading: isUpdateLoading } = useUpdate(
    queryKeys.v2VehicleOperationSchedule,
    ApiNamePaths.vehicleOperationScheduleApi.apiName,
    `${ApiNamePaths.vehicleOperationScheduleApi.apiPath}/${vehicleOperationScheduleId}`,
  );

  useEffect(() => {
    if (storybookMode && mockDrives) {
      setDrives(mockDrives);
      setIsButtonShow(true);

      return;
    }

    if (!isLoading) {
      if (error) {
        setErrorMessage(error?.message);

        return;
      }

      if (data) {
        const newData = getTimelineData(data.reservationList);
        setDrives((prevDrives) => ({ ...prevDrives, ...data, detail: newData }));

        if (data.actualOperationEndDateTime) {
          setIsButtonShow(false);
        } else {
          setIsButtonShow(true);
        }
      }
    }
  }, [data, error, isLoading, storybookMode, mockDrives]);

  const handleOperationStart = () => {
    const body = {};

    mutate(body, {
      onSuccess: () => {
        navigate(paths.driveList);
      },
      onError: (err) => {
        setErrorMessage(err instanceof Error ? err.message : '');
      },
    });
  };

  return (
    <>
      {errorMessage && <p className="operation-finish-confirm-error">{errorMessage}</p>}
      <div className="operation-finish-confirm-page">
        {drives && (
          <div className="operation-finish-confirm-card">
            <h1 className="operation-finish-confirm-title">{t('operationFinishConfirm.title')}</h1>

            <div className="operation-finish-confirm-detail">
              <div className="operation-finish-confirm-row">
                <span className="operation-finish-confirm-label">{t('operationFinishConfirm.dateLabel')}</span>
                <span className="operation-finish-confirm-value">{dayjs(drives.date).format('YYYY/MM/DD')}</span>
              </div>
              <div className="operation-finish-confirm-row">
                <span className="operation-finish-confirm-label">{t('operationFinishConfirm.areaLabel')}</span>
                <span className="operation-finish-confirm-value">{drives.operationZoneName}</span>
              </div>
              <div className="operation-finish-confirm-row">
                <span className="operation-finish-confirm-label">{t('operationFinishConfirm.directionLabel')}</span>
                <span className="operation-finish-confirm-value">{drives.direction}</span>
              </div>
              <div className="operation-finish-confirm-row">
                <span className="operation-finish-confirm-label">{t('operationFinishConfirm.scheduleLabel')}</span>
                <span className="operation-finish-confirm-value">{drives.operationScheduleName}</span>
              </div>

              <div className="operation-finish-confirm-row-waypoint-list">
                <span className="operation-finish-confirm-label operation-finish-confirm-label-waypoint-list">
                  運行経路
                </span>
                <div className="operation-finish-confirm-timeline-list">
                  {drives.detail?.map((item, index) => {
                    const total = drives.detail?.length ?? 0;
                    const label =
                      index === 0
                        ? t('operationFinishConfirm.departureLabel')
                        : index === total - 1
                          ? t('operationFinishConfirm.endLabel')
                          : t('operationFinishConfirm.waypointLabel', { number: index });
                    const checkflg = (item.pickup?.length ?? 0) > 0;

                    return (
                      <div
                        key={item.key}
                        className={
                          checkflg
                            ? 'operation-finish-confirm-timeline-block-pickup'
                            : 'operation-finish-confirm-timeline-block-dropoff'
                        }
                      >
                        <div className="operation-finish-confirm-timeline-left">
                          <span className="operation-finish-confirm-timeline-label">{label}</span>
                          <span className="operation-finish-confirm-timeline-time">
                            {dayjs(item.time).format('HH:mm')}
                          </span>
                        </div>
                        <div className="operation-finish-confirm-timeline-place">
                          {checkflg ? t('operationFinishConfirm.pickup') : t('operationFinishConfirm.dropoff')}{' '}
                          {item.waypointName}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="operation-finish-confirm-row">
                <span className="operation-finish-confirm-label">
                  {t('operationFinishConfirm.passengerCountLabel')}
                </span>
                <span className="operation-finish-confirm-value">
                  {t('operationFinishConfirm.reservationPassengerValue', {
                    reservationCount,
                    passengerCount,
                  })}
                </span>
              </div>
              <div className="operation-finish-confirm-row">
                <span className="operation-finish-confirm-label">{t('operationFinishConfirm.totalLabel')}</span>
                <span className="operation-finish-confirm-value">{totalAmount}円</span>
              </div>
            </div>

            <div className="operation-finish-confirm-message-box">
              <p className="operation-finish-confirm-message-text">{t('operationFinishConfirm.message')}</p>
            </div>

            <div className="operation-finish-confirm-actions">
              {isButtonShow && (
                <button
                  type="button"
                  className="operation-finish-confirm-btn"
                  disabled={isUpdateLoading}
                  onClick={handleOperationStart}
                >
                  {t('operationFinishConfirm.operationButton')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
